import axios from 'axios';
import {
  AuthTokenResponse,
  SendNotificationPayload,
  SendNotificationResponse,
  MetaWhatsAppAccount,
  WhatsAppTemplate,
  WhatsAppTemplateListResponse,
} from '../types';
import config from '../config';

const publicApiNotificationsClient = axios.create({
  baseURL: config.notificationsApiHost,
  timeout: 120000,
});

const googleAuthNotificationsClient = axios.create({
  baseURL: config.notificationsApiHost,
  timeout: 120000,
});

const addAuthInterceptor = (client: any) => {
  client.interceptors.request.use((reqConfig: any) => {
    const googleIdToken = localStorage.getItem('googleIdToken');
    if (googleIdToken) {
      reqConfig.headers['Authorization'] = `Bearer ${googleIdToken}`;
    }
    return reqConfig;
  });
};

addAuthInterceptor(googleAuthNotificationsClient);

const handleApiError = (error: any) => {
  if (error.response && error.response.status === 401) {
    if (error.config.url.includes('auth/token')) {
      console.error("JWT Auth Token request failed", error);
    } else {
      localStorage.removeItem('googleIdToken');
      window.dispatchEvent(new Event('auth-error'));
    }
  }
  return Promise.reject(error);
};

googleAuthNotificationsClient.interceptors.response.use((response: any) => response, handleApiError);


class ApiNotificationsClient {
  private static instance: ApiNotificationsClient;

  private constructor() { }

  public static getInstance(): ApiNotificationsClient {
    if (!ApiNotificationsClient.instance) {
      ApiNotificationsClient.instance = new ApiNotificationsClient();
    }
    return ApiNotificationsClient.instance;
  }

  private getHeaders(organizationId?: string) {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'x-api-key': config.apiKey,
    };

    if (organizationId) {
      headers['x-aida-org-id'] = organizationId;
    }

    return headers;
  }

  public async getBearerTokenForSendTemplatesByNotificationsService(clientId: string, clientSecret: string): Promise<AuthTokenResponse> {
    const response = await publicApiNotificationsClient.post('/api/auth/token/', {
      client_id: clientId,
      client_secret: clientSecret,
    });
    return response.data;
  }

  public async sendNotification(token: string, payload: SendNotificationPayload): Promise<SendNotificationResponse> {
    const response = await publicApiNotificationsClient.post('/api/send-notification/', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  public async sendMarketingMessage(token: string, payload: SendNotificationPayload): Promise<SendNotificationResponse> {
    const response = await publicApiNotificationsClient.post('/api/send-marketing-message/', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  public async sendBulkTemplate(token: string, origin: string, templateId: string, file: File, isMarketing: boolean, headerUrl?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('origin', origin);
    formData.append('template_id', templateId);
    formData.append('is_marketing', String(isMarketing));
    if (headerUrl) {
      formData.append('headerUrl', headerUrl);
    }

    const response = await publicApiNotificationsClient.post('/api/send-bulk-template/', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  public async getConnectionsForOrg(organizationId: string): Promise<string[]> {
    const response = await publicApiNotificationsClient.get('/api/connections/', {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getOrgWhatsAppAccounts(organizationId: string): Promise<MetaWhatsAppAccount[]> {
    const response = await googleAuthNotificationsClient.get(`/api/organizations/${organizationId}/whatsapp-accounts/`);
    return response.data;
  }

  public async getMessageTemplates(whatsappPhone: string): Promise<WhatsAppTemplateListResponse> {
    const response = await publicApiNotificationsClient.get('/api/meta/message-templates/', {
      params: { whatsapp_phone: whatsappPhone },
    });
    return response.data;
  }

  public async getMessageTemplateById(templateId: string, whatsappPhone: string): Promise<WhatsAppTemplate> {
    const response = await publicApiNotificationsClient.get(`/api/meta/templates/${templateId}/`, {
      params: { whatsapp_phone: whatsappPhone },
    });
    return response.data;
  }

  public async createMessageTemplate(whatsappPhone: string, templateData: any): Promise<any> {
    const response = await publicApiNotificationsClient.post('/api/meta/templates/', {
      whatsapp_phone: whatsappPhone,
      template: templateData,
    });
    return response.data;
  }

  public async updateMessageTemplate(templateId: string, whatsappPhone: string, components: any): Promise<any> {
    const response = await publicApiNotificationsClient.post(`/api/meta/templates/update/${templateId}/`, {
      whatsapp_phone: whatsappPhone,
      components: components,
    });
    return response.data;
  }

  public async deleteMessageTemplate(whatsappPhone: string, templateName: string): Promise<void> {
    await publicApiNotificationsClient.delete(`/api/meta/templates/${templateName}/delete/`, {
      params: { whatsapp_phone: whatsappPhone },
    });
  }

  public async createMediaHandle(publicUrl: string, whatsappPhone: string, metaAppId: string): Promise<{ handle: string }> {
    const payload = { public_url: publicUrl, whatsapp_phone: whatsappPhone, meta_app_id: metaAppId };
    const response = await googleAuthNotificationsClient.post('/api/meta/media-handle/', payload);
    return response.data;
  }

  public async onboardToMarketingApi(businessId: string): Promise<any> {
    const response = await googleAuthNotificationsClient.post('/api/meta/onboard-marketing/', {
      business_id: businessId,
    });
    return response.data;
  }
}

export default ApiNotificationsClient.getInstance();
